/**
 * @file   src\Pages\CustomerSupportDesk.tsx
 * @brief  Customer Support Desk page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Table, Badge, Link, Button, useOutletContext, useNavigate, useEffect, useState, Pagination, OverlayTrigger, Tooltip, React } from '../utils/thirdpartyComponents';
import CWSearchinput from '../components/CWSearchinput';
import { AppContextType } from '../interfaces/generalInterface';
import AddIcon from '../assets/images/icons/addicon';
import { pageURLs } from '../utils/pageUrls';
import { userType } from '../utils/enums';
import { TicketRecord } from '../interfaces/generalInterface';
import axios from '../config/axiosConfig';
import { TicketApi } from '../utils/apiUrls';
import moment from 'moment';
import SmallLoader from '../components/SmallLoader';

const CustomerSupportDesk = (props: any) => {
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [count, setCount] = useState({ openCount: 0, closedCount: 0 })
    const [ticketRecords, setTicketRecords] = useState<TicketRecord[]>([])

    // State to handle search
    const [searchValue, setSearchValue] = useState<string>('')

    // To display heading
    useEffect(() => {
        handleUpdate()

        // Get ticket list
        getTicketList(currentPage)

    }, [currentPage, searchValue])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Customer Support Desk", url: pageURLs.dashboard, showBackButton: true, showMainMenu: true, activeLink: "manage-car-wash" });
    };


    const getTicketList = async (pageNo = 1) => {
        try {

            let param;

            if (searchValue.length) {
                param = `?pageNo=${pageNo}&searchText=${searchValue}`
            } else {
                param = `?pageNo=${pageNo}`;
            }


            const { data } = props.userType == userType['CARWASH-OWNER'] ? await axios.get(TicketApi.CARWASH_OWNER_GET_TICKET_LIST + param) : await axios.get(TicketApi.ADMIN_GET_TICKET_LIST + param);
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const ticket_list = data.data.list;
                const count = { openCount: data.data.openCount, closedCount: data.data.closedCount }
                // Check if ticket_list is empty
                if (ticket_list.length > 0) {
                    setTicketRecords(ticket_list);
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                    setCount(count)
                } else {
                    console.log('Ticket list is empty');
                    setTicketRecords([]);  // Set to an empty array if ticket_list is empty
                }
            } else {
                console.log('Ticket list is not available or is not an array');
                setTicketRecords([]);  // Set to an empty array if data is not available
            }

        } catch (error: any) {
            console.error('Error fetching company list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
        finally {
            setLoading(false); // Hide loader
        }
    }

    // Triggers when input is entered in search
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        setCurrentPage(1)
    }

    const handleClearSearch = () => {
        setSearchValue('');
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const AddnewTicket = () => {
        if (props.userType === userType['CARWASH-OWNER']) {
            navigate(`/${pageURLs.cwo_addticket}`);
        }

    }

    const ViewTicket = (supportId: number) => {
        if (props.userType === userType['CARWASH-OWNER']) {
            navigate(`/${pageURLs.support_chat}/${supportId}`);
        }
        else {
            navigate(`/superadmin/${pageURLs.support_chat}/${supportId}`);
        }
    }

    // Truncate text function to limit the length of the description displayed
    const truncateText = (text: string, maxLength: number): string => {
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Render tooltip function for the full description
    const renderTooltip = (fullAddress: string) => {
        const TooltipComponent = (props: any) => (
            <Tooltip {...props}>{fullAddress}</Tooltip>
        );
        TooltipComponent.displayName = `Tooltip(${fullAddress})`; // Dynamic display name
        return TooltipComponent;
    };

    // To format date
    const formatDate = (reportedDate: string) => {
        const date = moment(reportedDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }

    return (

        <div className='manage-carwash-container order-list-container customer-support-wrap'>
            <Container>
                <div className="manage-header-block d-flex align-items-center">
                    {
                        props.userType !== userType['CARWASH-OWNER'] && (
                            <>
                                <div className="search-wrap order-search d-flex">
                                    <CWSearchinput
                                        id="search"
                                        name="search"
                                        type="text"
                                        placeholder=" Search..."
                                        value={searchValue}
                                        onChange={handleSearch}
                                        onClose={handleClearSearch}
                                    />
                                </div>
                                <div className="issue-status d-flex align-items-center">
                                    <div className="issue d-flex align-items-center">
                                        <h6 className='f-bold'>Open</h6>
                                        <div className="count open d-flex align-items-center justify-content-center">{count.openCount}</div>
                                    </div>
                                    <div className="issue d-flex align-items-center">
                                        <h6 className='f-bold'>Closed</h6>
                                        <div className="count closed d-flex align-items-center justify-content-center">{count.closedCount}</div>
                                    </div>
                                </div>
                            </>
                        )}

                    {props.userType == userType['CARWASH-OWNER'] ?
                        <Button
                            title="Add New Ticket"
                            type="submit"
                            variant="primary"
                            className="primary-btn ms-auto d-flex align-items-center"
                            onClick={AddnewTicket}
                        >
                            Add New Ticket
                            <AddIcon />
                        </Button> : null}
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Car Wash Location</th>
                                <th>Ticket Number</th>
                                <th>Reported Date</th>
                                <th>Subject</th>
                                <th>Description</th>
                                <th>Order Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ticketRecords.length > 0 ?
                                ticketRecords.map((record, index) => (
                                    <tr key={index}>
                                        <td width='12%'>{record.carwashlocation}</td>
                                        <td width='18%'>
                                            <Link to={props.userType === userType['CARWASH-OWNER'] ? `/${pageURLs.support_chat}/${record.customersupportid}` : `/superadmin/${pageURLs.support_chat}/${record.customersupportid}`} >{record.ticketnumber}</Link>
                                        </td>
                                        <td width='16%'>{formatDate(record.reporteddate)}</td>
                                        <td>{record.subject}</td>
                                        <td className='description'>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={renderTooltip(record.description)} // Pass the full Description to the tooltip
                                            >
                                                <span>{truncateText(record.description, 20)}</span>
                                            </OverlayTrigger>

                                        </td>
                                        <td width='18%'>
                                            <Badge className={record.orderstatus === "Closed" ? "warning" : "active"}>{record.orderstatus}</Badge>
                                        </td>
                                        <td className='nowrap'>
                                            <Button title="View Details" variant="outline-primary" className="outline-btn" onClick={() => ViewTicket(record.customersupportid)}>
                                                View Details
                                            </Button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                    </tr>
                                )}

                        </tbody>
                    </Table>

                    {ticketRecords.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                </div>
            </Container >

            {loading && <SmallLoader />}
        </div >
    )
}

export default CustomerSupportDesk;
